import type {
  PublicNoticesBundle,
  SiteNoticeKarinItem,
  SiteNoticeNearbyItem,
  SiteNoticeReceptionItem,
} from "../types/site-notices";
import type { NoticeShareFields } from "../lib/notice-share-snippet";
import { NoticeShareRow } from "./NoticeShareRow";
import { FanNoticeActions } from "./FanNoticeActions";
import {
  enableWebPush,
  fetchFanStatuses,
  getGlobalNotifyEnabled,
  getStoredSubscriptionId,
  patchFanStatus,
  setGlobalNotifyEnabled,
} from "../lib/fan-notifications-client";
import { useEffect, useMemo, useState } from "react";

function daysBetween(today: string, target: string | null | undefined): number | null {
  if (!target) return null;
  const a = today.slice(0, 10);
  const b = target.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return null;
  const da = Date.parse(`${a}T00:00:00Z`);
  const db = Date.parse(`${b}T00:00:00Z`);
  if (!Number.isFinite(da) || !Number.isFinite(db)) return null;
  return Math.round((db - da) / 86400000);
}

function GlobalNoticeToggle({ eventIds }: { eventIds: string[] }) {
  const ids = useMemo(() => [...new Set(eventIds.filter(Boolean))], [eventIds]);
  const [globalEnabled, setGlobalEnabled] = useState(() => getGlobalNotifyEnabled());
  const [enabledCount, setEnabledCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const hasEnabled = globalEnabled || enabledCount > 0;

  useEffect(() => {
    let alive = true;
    (async () => {
      const statuses = await fetchFanStatuses(ids);
      const enabledIds = ids.filter((id) => statuses[id]?.notificationEnabled);
      const subscriptionId = getStoredSubscriptionId();
      const shouldAutoEnable = getGlobalNotifyEnabled() && Boolean(subscriptionId);
      if (shouldAutoEnable && ids.length > 0 && enabledIds.length < ids.length) {
        await Promise.all(
          ids
            .filter((id) => !statuses[id]?.notificationEnabled)
            .map((eventId) => patchFanStatus({ eventId, notificationEnabled: true, subscriptionId })),
        );
        if (alive) setGlobalEnabled(true);
        if (alive) setEnabledCount(ids.length);
        return;
      }
      if (alive) setGlobalEnabled(getGlobalNotifyEnabled());
      if (alive) setEnabledCount(enabledIds.length);
    })();
    return () => {
      alive = false;
    };
  }, [ids]);

  const toggle = async () => {
    setBusy(true);
    setMsg(null);
    try {
      let subscriptionId = getStoredSubscriptionId();
      const nextEnabled = !hasEnabled;
      if (nextEnabled) {
        const push = await enableWebPush();
        if (push.error) {
          setMsg(push.error);
          return;
        }
        subscriptionId = push.subscriptionId ?? getStoredSubscriptionId();
      }
      setGlobalNotifyEnabled(nextEnabled);
      setGlobalEnabled(nextEnabled);
      if (ids.length) {
        await Promise.all(ids.map((eventId) => patchFanStatus({ eventId, notificationEnabled: nextEnabled, subscriptionId })));
      }
      setEnabledCount(nextEnabled ? ids.length : 0);
      setMsg(nextEnabled ? (ids.length ? "通知はオンです" : "通知はオンです。予定が出たら通知対象にします。") : "通知をオフにしました。");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`home-notice-global-toggle ${hasEnabled ? "home-notice-global-toggle--on" : "home-notice-global-toggle--off"}`}>
      <button
        type="button"
        className={`btn small home-notice-global-toggle-btn ${hasEnabled ? "home-notice-global-toggle-btn--on" : "home-notice-global-toggle-btn--off"}`}
        disabled={busy}
        onClick={() => void toggle()}
        aria-pressed={hasEnabled}
      >
        {busy ? "設定中…" : hasEnabled ? "通知はオンです" : "通知はオフです"}
      </button>
      <span className="home-notice-global-toggle-note">
        {ids.length
          ? hasEnabled
            ? `この欄の予定通知は基本オンです（${enabledCount}/${ids.length}件）`
            : "押すと、この欄の予定通知をまとめてオンにします"
          : hasEnabled
            ? "通知はオンです。通知対象の予定が出たら自動で対象にします"
            : "今は通知対象の予定がありません。先に通知だけオンにできます"}
      </span>
      {msg ? <span className="home-notice-global-toggle-msg">{msg}</span> : null}
    </div>
  );
}

function toShareFields(
  item: Pick<
    SiteNoticeKarinItem | SiteNoticeReceptionItem,
    "event_name" | "event_date" | "venue" | "showTime" | "linkUrl" | "label"
  > & { deadlineDate?: string | null; receptionEnd?: string | null },
): NoticeShareFields {
  return {
    event_name: item.event_name,
    event_date: item.event_date,
    venue: item.venue,
    showTime: item.showTime,
    linkUrl: item.linkUrl,
    label: "label" in item ? item.label : undefined,
    deadlineDate: "deadlineDate" in item ? item.deadlineDate : undefined,
    receptionEnd: "receptionEnd" in item ? item.receptionEnd : undefined,
  };
}

function KarinNoticeCard({
  item,
  nearby,
  today,
}: {
  item: SiteNoticeKarinItem;
  nearby: SiteNoticeNearbyItem[];
  today: string;
}) {
  const emph = item.emphasis;
  return (
    <article className={`home-notice-card home-notice-card--karin home-notice-card--${emph}`}>
      <div className="home-notice-card-top">
        <span className="home-notice-badge">{item.label}</span>
        <NoticeShareRow fields={toShareFields(item)} />
      </div>
      <div className="home-notice-card-body">
        <h3 className="home-notice-title">{item.event_name}</h3>
        <p className="home-notice-meta mono">
          {item.event_date}
          {item.venue ? ` ・ ${item.venue}` : ""}
          {item.showTime ? ` ・ ${item.showTime}` : ""}
          {item.deadlineDate ? ` ・ 締切 ${item.deadlineDate}` : ""}
        </p>
        <FanNoticeActions
          eventId={item.eventId}
          eventName={item.event_name}
          mode={item.kind === "deadline_soon" ? "deadline" : "event"}
          daysUntilDeadline={daysBetween(today, item.deadlineDate)}
          daysUntilEvent={daysBetween(today, item.event_date)}
        />
      </div>
      {nearby.length > 0 ? (
        <section className="home-notice-nearby">
          <h4 className="home-notice-nearby-title">近くで開催中</h4>
          <ul className="home-notice-nearby-list">
            {nearby.map((n) => (
              <li key={n.noticeKey} className="home-notice-nearby-item">
                <div className="home-notice-nearby-head">
                  <span className="home-notice-nearby-perf">{n.performers.join(" / ") || "—"}</span>
                  {n.linkUrl ? (
                    <a className="home-notice-link-btn home-notice-link-btn--sm" href={n.linkUrl} target="_blank" rel="noreferrer">
                      🔗
                    </a>
                  ) : null}
                </div>
                <div className="home-notice-nearby-name">{n.event_name}</div>
                <div className="home-notice-nearby-meta mono">
                  {n.event_date}
                  {n.venue ? ` ・ ${n.venue}` : ""}
                  {n.showTime ? ` ・ ${n.showTime}` : ""}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}

function ReceptionNoticeCard({ item, today }: { item: SiteNoticeReceptionItem; today: string }) {
  return (
    <article className="home-notice-card home-notice-card--reception">
      <div className="home-notice-card-top">
        <span className="home-notice-badge home-notice-badge--soft">受付中</span>
        <NoticeShareRow fields={toShareFields({ ...item, label: "受付中" })} />
      </div>
      <div className="home-notice-card-body">
        <h3 className="home-notice-title">{item.event_name}</h3>
        <p className="home-notice-meta mono">
          {item.event_date}
          {item.venue ? ` ・ ${item.venue}` : ""}
          {item.showTime ? ` ・ ${item.showTime}` : ""}
          {item.receptionEnd ? ` ・ 受付終了 ${item.receptionEnd}` : ""}
        </p>
        <FanNoticeActions
          eventId={item.eventId}
          eventName={item.event_name}
          mode="reception"
          daysUntilDeadline={daysBetween(today, item.receptionEnd)}
          daysUntilEvent={daysBetween(today, item.event_date)}
        />
      </div>
    </article>
  );
}

type Props = {
  notices: PublicNoticesBundle | null;
};

export function HomeNoticeBlocks({ notices }: Props) {
  if (!notices) return null;
  const urgent = notices.urgent.items;
  const reception = notices.reception.items;
  const showUrgent = urgent.length > 0;
  const showReception = reception.length > 0;
  const noticeEventIds = [...urgent.map((item) => item.eventId), ...reception.map((item) => item.eventId)];

  return (
    <div className="home-notice-blocks">
      <GlobalNoticeToggle eventIds={noticeEventIds} />
      {!showUrgent && !showReception ? <p className="home-notice-empty">今すぐ通知する予定はありません。通知ONにしておくと、対象予定が出た時に受け取れます。</p> : null}
      {showUrgent ? (
        <section className="home-notice-block card">
          <h2 className="home-notice-block-title">まもなく締切・まもなく開催</h2>
          <div className="home-notice-block-list">
            {urgent.map((item) => (
              <KarinNoticeCard key={item.noticeKey} item={item} today={notices.today} nearby={notices.urgent.nearbyByParent[item.eventId] ?? []} />
            ))}
          </div>
        </section>
      ) : null}
      {showReception ? (
        <section className="home-notice-block card home-notice-block--reception">
          <h2 className="home-notice-block-title">現在受付中</h2>
          <div className="home-notice-block-list">
            {reception.map((item) => (
              <ReceptionNoticeCard key={item.noticeKey} item={item} today={notices.today} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
